import React, { useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { CURVE_WIDTH, R_SEG, L_SEG } from '../../navigation/TabBar/tabBarConstants'
import { onAppEvent } from '../../../lib/events'
import { computeInactivePillRect, INACTIVE_PILL_HEIGHT } from './inactivePillGeometry'
import './ContentUnionShape.scss'

interface ContentUnionShapeProps {
  contentRef: React.RefObject<HTMLDivElement | null>
  activeTabId?: string
  isFirstTabActive?: boolean
  isPreview?: boolean
  sidebarLeftOpen?: boolean
  sidebarRightOpen?: boolean
  tabCount?: number
  pinnedCount?: number
  /** DOM element at window-frame level (before titlebar) where bump layers are portaled */
  bumpSlotEl?: HTMLDivElement | null
  /** DOM element at window-frame level (after content) where border SVG is portaled */
  borderSlotEl?: HTMLDivElement | null
}

const R = 12 // Border-radius for content corners
const K = 0.5522847 // Bezier approximation constant for quarter circle
const BUMP_INSET = 28 // Pixels to inset the bump from each side of the tab element
const ANIM_DURATION = 350 // Tab switch animation in ms

type Rect = { x: number; y: number; w: number; h: number }

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

function lerpRect(a: Rect, b: Rect, t: number): Rect {
  return {
    x: lerp(a.x, b.x, t),
    y: lerp(a.y, b.y, t),
    w: lerp(a.w, b.w, t),
    h: lerp(a.h, b.h, t),
  }
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3)
}

/**
 * Builds a closed SVG path for the union of active tab + content rectangle.
 * Returns empty string if content rect is not available.
 */
function buildUnionPath(
  content: { x: number; y: number; w: number; h: number },
  tab: { x: number; y: number; w: number; h: number } | null,
  isFirstTab: boolean,
): string {
  const { x: cX, y: cY, w: cW, h: cH } = content

  if (cW <= 0 || cH <= 0) return ''

  // No visible tab → simple rounded rectangle
  if (!tab) {
    return roundedRect(cX, cY, cW, cH, R)
  }

  const tabRight = tab.x + tab.w
  const tabTop = tab.y
  const tabHeight = cY - tabTop // Distance from tab top to content top

  if (tabHeight <= 0) {
    return roundedRect(cX, cY, cW, cH, R)
  }

  // Inset the effective tab edges to make the bump narrower
  const effectiveTabX = tab.x + BUMP_INSET
  const effectiveTabRight = tabRight - BUMP_INSET
  const effectiveTabW = effectiveTabRight - effectiveTabX

  // Safety: if tab is too narrow for inset, skip bump
  if (effectiveTabW < 0) {
    return roundedRect(cX, cY, cW, cH, R)
  }

  const CW = CURVE_WIDTH
  const cornerR = 12

  // Auto-detect overflow: use simple corner when S-curve would exceed content bounds.
  // Both sides use purely geometric detection so the transition happens
  // gradually as the bump slides toward the edge (no sudden snap).
  const simpleLeft = (effectiveTabX - CW < cX)
  const simpleRight = (effectiveTabRight + CW > cX + cW)

  if (simpleLeft && simpleRight) {
    // Both sides: simple 12px corners (no S-curves)
    const contentRight = cX + cW
    return [
      `M ${cX},${cY}`,
      `L ${cX},${tabTop + cornerR}`,
      `C ${cX},${tabTop + cornerR * (1 - K)} ${cX + cornerR * (1 - K)},${tabTop} ${cX + cornerR},${tabTop}`,
      `L ${contentRight - cornerR},${tabTop}`,
      `C ${contentRight - cornerR + cornerR * K},${tabTop} ${contentRight},${tabTop + cornerR * (1 - K)} ${contentRight},${tabTop + cornerR}`,
      `L ${contentRight},${cY + cH - R}`,
      `C ${contentRight},${cY + cH - R + R * K} ${contentRight - R + R * K},${cY + cH} ${contentRight - R},${cY + cH}`,
      `L ${cX + R},${cY + cH}`,
      `C ${cX + R - R * K},${cY + cH} ${cX},${cY + cH - R + R * K} ${cX},${cY + cH - R}`,
      `L ${cX},${cY}`,
      'Z',
    ].join(' ')
  }

  if (simpleLeft) {
    // Left: simple 12px corner, Right: S-curve
    return [
      `M ${cX},${cY}`,
      `L ${cX},${tabTop + cornerR}`,
      `C ${cX},${tabTop + cornerR * (1 - K)} ${cX + cornerR * (1 - K)},${tabTop} ${cX + cornerR},${tabTop}`,
      `L ${effectiveTabRight},${tabTop}`,
      ...R_SEG.map(s => {
        const sx = effectiveTabRight + s.c1x * CW
        const sy = tabTop + s.c1y * tabHeight
        const cx = effectiveTabRight + s.c2x * CW
        const cy = tabTop + s.c2y * tabHeight
        const ex = effectiveTabRight + s.ex * CW
        const ey = tabTop + s.ey * tabHeight
        return `C ${sx},${sy} ${cx},${cy} ${ex},${ey}`
      }),
      `L ${cX + cW - R},${cY}`,
      `C ${cX + cW - R + R * K},${cY} ${cX + cW},${cY + R * (1 - K)} ${cX + cW},${cY + R}`,
      `L ${cX + cW},${cY + cH - R}`,
      `C ${cX + cW},${cY + cH - R + R * K} ${cX + cW - R + R * K},${cY + cH} ${cX + cW - R},${cY + cH}`,
      `L ${cX + R},${cY + cH}`,
      `C ${cX + R - R * K},${cY + cH} ${cX},${cY + cH - R + R * K} ${cX},${cY + cH - R}`,
      `L ${cX},${cY}`,
      'Z',
    ].join(' ')
  }

  if (simpleRight) {
    // Left: S-curve, Right: simple 12px corner
    const tabLeftEdge = effectiveTabX - CW
    const contentRight = cX + cW
    return [
      `M ${cX + R},${cY}`,
      `C ${cX + R - R * K},${cY} ${cX},${cY + R * (1 - K)} ${cX},${cY + R}`,
      `L ${cX},${cY + cH - R}`,
      `C ${cX},${cY + cH - R + R * K} ${cX + R - R * K},${cY + cH} ${cX + R},${cY + cH}`,
      `L ${contentRight - R},${cY + cH}`,
      `C ${contentRight - R + R * K},${cY + cH} ${contentRight},${cY + cH - R + R * K} ${contentRight},${cY + cH - R}`,
      `L ${contentRight},${tabTop + cornerR}`,
      `C ${contentRight},${tabTop + cornerR * (1 - K)} ${contentRight - cornerR + cornerR * K},${tabTop} ${contentRight - cornerR},${tabTop}`,
      `L ${effectiveTabX},${tabTop}`,
      ...reverseSCurve(L_SEG, tabLeftEdge, tabTop, CW, tabHeight),
      `L ${cX + R},${cY}`,
      'Z',
    ].join(' ')
  }

  // Normal: left S-curve, right S-curve
  const tabLeftEdge = effectiveTabX - CW
  return [
    `M ${cX + R},${cY}`,
    `C ${cX + R - R * K},${cY} ${cX},${cY + R * (1 - K)} ${cX},${cY + R}`,
    `L ${cX},${cY + cH - R}`,
    `C ${cX},${cY + cH - R + R * K} ${cX + R - R * K},${cY + cH} ${cX + R},${cY + cH}`,
    `L ${cX + cW - R},${cY + cH}`,
    `C ${cX + cW - R + R * K},${cY + cH} ${cX + cW},${cY + cH - R + R * K} ${cX + cW},${cY + cH - R}`,
    `L ${cX + cW},${cY + R}`,
    `C ${cX + cW},${cY + R * (1 - K)} ${cX + cW - R + R * K},${cY} ${cX + cW - R},${cY}`,
    `L ${effectiveTabRight + CW},${cY}`,
    ...reverseSCurve(R_SEG, effectiveTabRight, tabTop, CW, tabHeight),
    `L ${effectiveTabX},${tabTop}`,
    ...reverseSCurve(L_SEG, tabLeftEdge, tabTop, CW, tabHeight),
    `L ${cX + R},${cY}`,
    'Z',
  ].join(' ')
}

/** Reverse S-curve segments for tracing the path in the opposite direction */
function reverseSCurve(
  segments: readonly { c1x: number; c1y: number; c2x: number; c2y: number; ex: number; ey: number }[],
  originX: number,
  originY: number,
  curveWidth: number,
  height: number,
): string[] {
  // Walk segments in reverse, swapping control points
  const result: string[] = []
  for (let i = segments.length - 1; i >= 0; i--) {
    const s = segments[i]
    const prev = i > 0 ? segments[i - 1] : { ex: 0, ey: segments === R_SEG ? 0 : 1 }

    // For R_SEG: origin is at (0,0)=tab-top-right, (1,1)=content-level
    // For L_SEG: origin is at (0,1)=content-level, (1,0)=tab-top-left
    const c1x = originX + s.c2x * curveWidth
    const c1y = originY + s.c2y * height
    const c2x = originX + s.c1x * curveWidth
    const c2y = originY + s.c1y * height
    const ex = originX + prev.ex * curveWidth
    const ey = originY + prev.ey * height

    result.push(`C ${c1x},${c1y} ${c2x},${c2y} ${ex},${ey}`)
  }
  return result
}

/** Simple rounded rectangle path */
function roundedRect(x: number, y: number, w: number, h: number, r: number): string {
  return [
    `M ${x + r},${y}`,
    `L ${x + w - r},${y}`,
    `C ${x + w - r + r * K},${y} ${x + w},${y + r * (1 - K)} ${x + w},${y + r}`,
    `L ${x + w},${y + h - r}`,
    `C ${x + w},${y + h - r + r * K} ${x + w - r + r * K},${y + h} ${x + w - r},${y + h}`,
    `L ${x + r},${y + h}`,
    `C ${x + r - r * K},${y + h} ${x},${y + h - r + r * K} ${x},${y + h - r}`,
    `L ${x},${y + r}`,
    `C ${x},${y + r * (1 - K)} ${x + r - r * K},${y} ${x + r},${y}`,
    'Z',
  ].join(' ')
}

const ContentUnionShapeComponent: React.FC<ContentUnionShapeProps> = ({
  contentRef,
  activeTabId,
  isFirstTabActive = false,
  isPreview = false,
  sidebarLeftOpen,
  sidebarRightOpen,
  tabCount,
  pinnedCount,
  bumpSlotEl,
  borderSlotEl,
}) => {
  const bgPathRef = useRef<SVGPathElement>(null)
  const borderPathRef = useRef<SVGPathElement>(null)
  const innerShadowPathRef = useRef<SVGPathElement>(null)
  const innerShadowClipRef = useRef<SVGPathElement>(null)
  const gradientRef = useRef<SVGLinearGradientElement>(null)
  const tabBgPillRef = useRef<HTMLDivElement>(null)
  const inactivePillsRef = useRef<HTMLDivElement>(null)

  // Animation state
  const displayedTabRef = useRef<Rect | null>(null)
  const prevTabIdRef = useRef<string | undefined>(undefined)
  const animFrameRef = useRef<number>(0)
  const isAnimatingRef = useRef(false)
  const isFirstTabRef = useRef(isFirstTabActive)
  isFirstTabRef.current = isFirstTabActive
  const prevTabCountRef = useRef(tabCount)

  // Measure content + tab positions relative to frame.
  // When useLayoutPosition is true, the active tab's position is computed via
  // offsetLeft/offsetTop (immune to CSS transforms such as DnD-Kit FLIP animations).
  // This gives the tab's final layout position even during transform transitions.
  // Default (false) uses getBoundingClientRect which includes CSS transforms —
  // needed during drag so the bump follows the visually-moving tab.
  const measure = useCallback((opts?: { useLayoutPosition?: boolean }): {
    content: Rect
    tab: Rect | null
  } | null => {
    const frameEl = document.querySelector('.window-frame, .web-frame')
    const contentEl = contentRef.current
    if (!frameEl || !contentEl) return null

    const frameRect = frameEl.getBoundingClientRect()
    const contentRect = contentEl.getBoundingClientRect()

    const content: Rect = {
      x: contentRect.left - frameRect.left,
      y: contentRect.top - frameRect.top,
      w: contentRect.width,
      h: contentRect.height,
    }

    const tabEl = document.querySelector('.tab-bar__tab--active') as HTMLElement | null
    let tab: Rect | null = null

    if (tabEl) {
      if (opts?.useLayoutPosition && tabEl.offsetParent) {
        const parent = tabEl.offsetParent as HTMLElement
        const parentRect = parent.getBoundingClientRect()
        tab = {
          x: parentRect.left - frameRect.left + tabEl.offsetLeft,
          y: parentRect.top - frameRect.top + tabEl.offsetTop,
          w: tabEl.offsetWidth,
          h: tabEl.offsetHeight,
        }
      } else {
        const tabRect = tabEl.getBoundingClientRect()
        tab = {
          x: tabRect.left - frameRect.left,
          y: tabRect.top - frameRect.top,
          w: tabRect.width,
          h: tabRect.height,
        }
      }
    }

    return { content, tab }
  }, [contentRef])

  // Position the tab background pill at the active tab's measured rect.
  // The pill is rendered before the SVG in the bump layer (DOM order),
  // so the bump fill naturally covers it.
  const updateTabBgPill = useCallback((tab: Rect | null) => {
    const el = tabBgPillRef.current
    if (!el) return
    if (!tab) {
      el.style.display = 'none'
      return
    }
    el.style.display = ''
    el.style.left = `${tab.x}px`
    el.style.top = `${tab.y}px`
    el.style.width = `${tab.w}px`
    el.style.height = `${tab.h}px`
  }, [])

  // Sync inactive tab background pills into the bump-slot layer.
  // These render BEFORE the bump SVG (DOM order within the slot), so the bump
  // naturally covers them. This separates tab backgrounds from the tab elements,
  // allowing tabs to have z-index:6 (always above bump) while their backgrounds
  // stay below the bump.
  const updateInactiveTabPills = useCallback(() => {
    const container = inactivePillsRef.current
    if (!container) return

    const frameEl = document.querySelector('.window-frame, .web-frame')
    if (!frameEl) return
    const frameRect = frameEl.getBoundingClientRect()

    const allTabs = document.querySelectorAll('.tab-bar__tab') as NodeListOf<HTMLElement>

    // Sync pill element count to tab count
    while (container.children.length < allTabs.length) {
      const pill = document.createElement('div')
      pill.className = 'content-union-shape__inactive-pill content-union-shape__inactive-pill--animated'
      container.appendChild(pill)
    }
    while (container.children.length > allTabs.length) {
      container.removeChild(container.lastChild!)
    }

    // Pill geometry uses `tab.getBoundingClientRect()` (see
    // `inactivePillGeometry.ts`) so the pill follows DnD-Kit's transforms
    // during drag/sort. Y position stays anchored to the tab container's
    // bottom edge to avoid bouncing during the active-tab bump animation.
    const tabsContainer = document.querySelector('.tab-bar__tabs') as HTMLElement | null
    const tabsContainerRect = tabsContainer?.getBoundingClientRect() ?? null

    allTabs.forEach((tab, i) => {
      const pill = container.children[i] as HTMLDivElement
      const isActive = tab.classList.contains('tab-bar__tab--active')
      const isTearingOff = tab.classList.contains('tab-bar__tab--tearing-off')

      if (isActive || isTearingOff) {
        pill.style.display = 'none'
        return
      }

      const rect = computeInactivePillRect(tab, frameRect, tabsContainerRect)
      if (!rect) {
        pill.style.display = 'none'
        return
      }

      pill.style.display = ''
      pill.style.left = `${rect.x}px`
      pill.style.top = `${rect.y}px`
      pill.style.width = `${rect.w}px`
      pill.style.height = `${INACTIVE_PILL_HEIGHT}px`

      // Set border-radius from class names (NOT getComputedStyle — that reads
      // mid-transition values and causes the pill to flash rectangular).
      if (tab.classList.contains('tab-bar__tab--left-of-active')) {
        pill.style.borderRadius = '42px 16px 64px 42px'
      } else if (tab.classList.contains('tab-bar__tab--right-of-active')) {
        pill.style.borderRadius = '16px 42px 42px 64px'
      } else {
        pill.style.borderRadius = '42px'
      }
    })
  }, [])

  // Apply path to SVG elements and update gradient position.
  // Does NOT update the pill — the pill must always be at the final tab position,
  // not the interpolated animation position (see animated tab switch below).
  const applyPath = useCallback((content: Rect, tab: Rect | null, isFirst: boolean) => {
    const path = buildUnionPath(content, tab, isFirst)
    bgPathRef.current?.setAttribute('d', path)
    borderPathRef.current?.setAttribute('d', path)
    innerShadowPathRef.current?.setAttribute('d', path)
    innerShadowClipRef.current?.setAttribute('d', path)
    // Position gradient from tab top into content area (bump + 80px bleed)
    if (gradientRef.current && tab) {
      gradientRef.current.setAttribute('y1', String(tab.y))
      gradientRef.current.setAttribute('y2', String(content.y + 80))
    }
  }, [])

  // Immediate path update (skipped during animation).
  // Uses layout position so the bump snaps to the tab's final position even
  // when DnD-Kit FLIP transforms are active (e.g. right after a tab reorder).
  const updatePath = useCallback(() => {
    if (isAnimatingRef.current) return

    const m = measure({ useLayoutPosition: true })
    if (!m) return

    displayedTabRef.current = m.tab
    applyPath(m.content, m.tab, isFirstTabActive)
    updateTabBgPill(m.tab)
    updateInactiveTabPills()
  }, [measure, isFirstTabActive, applyPath, updateTabBgPill, updateInactiveTabPills])

  // Animated tab switch
  useEffect(() => {
    if (prevTabIdRef.current === undefined) {
      prevTabIdRef.current = activeTabId
      return
    }
    if (prevTabIdRef.current === activeTabId) return
    prevTabIdRef.current = activeTabId

    const from = displayedTabRef.current ? { ...displayedTabRef.current } : null
    if (!from) {
      updatePath()
      return
    }

    isAnimatingRef.current = true

    // Wait one extra frame so React has committed the new .tab-bar__tab--active
    // class before we start measuring. Without this, measure() on web can find
    // the old tab element and animate to the wrong target.
    cancelAnimationFrame(animFrameRef.current)
    animFrameRef.current = requestAnimationFrame(() => {
      const startTime = performance.now()

      // Position pill at the NEW tab immediately — provides gray background
      // while the bump slides over from the old position.
      const initialMeasure = measure()
      if (initialMeasure?.tab) {
        updateTabBgPill(initialMeasure.tab)
      }
      // Sync inactive tab pills (active tab changed)
      updateInactiveTabPills()

      const animate = () => {
        const m = measure()
        if (!m || !m.tab) {
          isAnimatingRef.current = false
          if (m) {
            displayedTabRef.current = m.tab
            applyPath(m.content, m.tab, isFirstTabRef.current)
            updateTabBgPill(m.tab)
          }
          return
        }

        const elapsed = performance.now() - startTime
        const progress = Math.min(elapsed / ANIM_DURATION, 1)
        const t = easeOutCubic(progress)

        const interp = lerpRect(from, m.tab, t)

        displayedTabRef.current = interp
        applyPath(m.content, interp, isFirstTabRef.current)

        if (progress < 1) {
          animFrameRef.current = requestAnimationFrame(animate)
        } else {
          isAnimatingRef.current = false
          displayedTabRef.current = m.tab
          applyPath(m.content, m.tab, isFirstTabRef.current)
          updateTabBgPill(m.tab)
          updateInactiveTabPills()
          // Final layout-position measurement after CSS transitions have settled
          animFrameRef.current = requestAnimationFrame(() => {
            if (isAnimatingRef.current) return
            const final = measure({ useLayoutPosition: true })
            if (final) {
              displayedTabRef.current = final.tab
              applyPath(final.content, final.tab, isFirstTabRef.current)
              updateTabBgPill(final.tab)
              updateInactiveTabPills()
            }
          })
        }
      }

      // Start immediately — no second rAF delay
      animate()
    })

    return () => {
      cancelAnimationFrame(animFrameRef.current)
      isAnimatingRef.current = false
    }
  }, [activeTabId, measure, applyPath, updatePath, updateTabBgPill, updateInactiveTabPills])

  // Update on mount and when dependencies change.
  // The forced re-measure at 300ms must bypass the animation guard so it always
  // applies the correct final width even if the animated switch is still running.
  useEffect(() => {
    updatePath()
    const timer = setTimeout(() => {
      const m = measure({ useLayoutPosition: true })
      if (m) {
        isAnimatingRef.current = false
        displayedTabRef.current = m.tab
        applyPath(m.content, m.tab, isFirstTabRef.current)
        updateTabBgPill(m.tab)
        updateInactiveTabPills()
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [updatePath, activeTabId, sidebarLeftOpen, tabCount, pinnedCount, measure, applyPath])

  // ResizeObserver on content element
  useEffect(() => {
    const el = contentRef.current
    if (!el) return
    const observer = new ResizeObserver(() => {
      try { updatePath() } catch { /* ignore resize observer errors */ }
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [contentRef, updatePath])

  // ResizeObserver on tabs container (tracks flex width changes)
  useEffect(() => {
    const tabsContainer = document.querySelector('.tab-bar__tabs') as HTMLElement
    if (!tabsContainer) return
    const observer = new ResizeObserver(() => {
      try { updatePath() } catch { /* ignore resize observer errors */ }
    })
    observer.observe(tabsContainer)
    return () => observer.disconnect()
  }, [updatePath, activeTabId])

  // ResizeObserver on active tab element (tracks width changes from in-place title updates).
  // Deferred by one rAF so React has committed the new active class to the DOM.
  useEffect(() => {
    let observer: ResizeObserver | null = null
    const rafId = requestAnimationFrame(() => {
      const tabEl = document.querySelector('.tab-bar__tab--active') as HTMLElement
      if (!tabEl) return
      observer = new ResizeObserver(() => {
        try { updatePath() } catch { /* ignore resize observer errors */ }
      })
      observer.observe(tabEl)
    })
    return () => {
      cancelAnimationFrame(rafId)
      observer?.disconnect()
    }
  }, [updatePath, activeTabId])

  // Helpers to temporarily disable pill transitions (prevents wobbling during
  // window resize, life space switches, and other rapid layout changes).
  const disablePillTransitions = useCallback(() => {
    const container = inactivePillsRef.current
    if (!container) return
    for (const child of container.children) {
      (child as HTMLElement).classList.remove('content-union-shape__inactive-pill--animated')
    }
  }, [])

  const enablePillTransitions = useCallback(() => {
    const container = inactivePillsRef.current
    if (!container) return
    for (const child of container.children) {
      (child as HTMLElement).classList.add('content-union-shape__inactive-pill--animated')
    }
  }, [])

  // Window resize — disable pill transitions, re-enable after settle (150ms debounce).
  useEffect(() => {
    let resizeTimer = 0

    const onResize = () => {
      disablePillTransitions()
      updatePath()
      clearTimeout(resizeTimer)
      resizeTimer = window.setTimeout(enablePillTransitions, 150)
    }

    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
      clearTimeout(resizeTimer)
    }
  }, [updatePath, disablePillTransitions, enablePillTransitions])

  // Life space switch — when tab count changes, the entire tab set is replaced.
  // Disable pill transitions so pills snap to new positions without wobbling.
  useEffect(() => {
    if (prevTabCountRef.current !== tabCount) {
      prevTabCountRef.current = tabCount
      disablePillTransitions()
      const timer = setTimeout(enablePillTransitions, 150)
      return () => clearTimeout(timer)
    }
  }, [tabCount, disablePillTransitions, enablePillTransitions])

  // Sidebar toggle — disable pill transitions so pills snap to new positions
  // without wobbling during the sidebar CSS transition.
  useEffect(() => {
    disablePillTransitions()
    const timer = setTimeout(enablePillTransitions, 300)
    return () => clearTimeout(timer)
  }, [sidebarLeftOpen, sidebarRightOpen, disablePillTransitions, enablePillTransitions])

  // Follow active tab during drag (bump tracks X position).
  // Separate handlers for move vs end: move uses visual position (getBoundingClientRect)
  // so the bump follows the dragged tab; end uses layout position (offsetLeft) so the
  // bump snaps to the tab's final position even during DnD-Kit FLIP animations.
  // Cancels any ongoing tab-switch animation — drag/cancel events take priority.
  useEffect(() => {
    let moveRafId = 0
    let endRafId = 0
    // Track whether a drag is currently active so we can disable pill
    // transitions exactly once on drag-start and re-enable on drag-end.
    // Without this, the pill's 0.35s transition lags behind the dragged or
    // FLIP'd tab — the tab visually moves but its pill chases and the user
    // sees mismatched, displaced pills underneath each tab.
    let dragActive = false

    const cancelAnimation = () => {
      if (isAnimatingRef.current) {
        cancelAnimationFrame(animFrameRef.current)
        isAnimatingRef.current = false
      }
    }

    const onDragMove = () => {
      if (!dragActive) {
        dragActive = true
        disablePillTransitions()
      }
      if (moveRafId) return
      moveRafId = requestAnimationFrame(() => {
        moveRafId = 0
        cancelAnimation()
        const m = measure()
        if (!m) return
        displayedTabRef.current = m.tab
        const tabEl = document.querySelector('.tab-bar__tab--active') as HTMLElement | null
        const isFirst = tabEl?.classList.contains('tab-bar__tab--first-active') ?? isFirstTabRef.current
        applyPath(m.content, m.tab, isFirst)
        updateTabBgPill(m.tab)
        updateInactiveTabPills()
      })
    }

    const onDragEnd = () => {
      dragActive = false
      if (endRafId) return
      endRafId = requestAnimationFrame(() => {
        endRafId = 0
        cancelAnimation()
        // Layout position: immune to DnD-Kit FLIP transforms after reorder
        const m = measure({ useLayoutPosition: true })
        if (!m) return
        displayedTabRef.current = m.tab
        const tabEl = document.querySelector('.tab-bar__tab--active') as HTMLElement | null
        const isFirst = tabEl?.classList.contains('tab-bar__tab--first-active') ?? isFirstTabRef.current
        applyPath(m.content, m.tab, isFirst)
        updateTabBgPill(m.tab)
        updateInactiveTabPills()
        // Re-enable transitions on the next frame so the just-set positions
        // settle before the CSS transition rule comes back.
        requestAnimationFrame(() => enablePillTransitions())
      })
    }

    const unsubMove = onAppEvent('tab-drag-move', onDragMove)
    const unsubEnd = onAppEvent('tab-drag-end', onDragEnd)
    return () => {
      cancelAnimationFrame(moveRafId)
      cancelAnimationFrame(endRafId)
      unsubMove()
      unsubEnd()
    }
  }, [measure, applyPath, updateInactiveTabPills, updateTabBgPill, disablePillTransitions, enablePillTransitions])

  // Re-measure after sidebar transition (200ms CSS transition)
  useEffect(() => {
    const timer = setTimeout(updatePath, 220)
    return () => clearTimeout(timer)
  }, [sidebarLeftOpen, updatePath])

  // Cleanup animation on unmount
  useEffect(() => {
    return () => cancelAnimationFrame(animFrameRef.current)
  }, [])

  // Split rendering: bump layers (pill, bg, inner-shadow) go into the bump-slot
  // BEFORE the titlebar in DOM order. Border goes into the border-slot AFTER content.
  // DOM-order stacking: bump (1st) < titlebar/labels (2nd) < content (3rd) < border (4th).

  const bumpContent = (
    <div className="content-union-shape content-union-shape--bump">
      {/* Inactive tab background pills: positioned before bump SVG so bump covers them */}
      <div ref={inactivePillsRef} className="content-union-shape__inactive-pills" />
      {/* Active tab background pill: gray fill, covered by bump SVG (DOM order within layer) */}
      <div ref={tabBgPillRef} className="content-union-shape__tab-bg-pill" />
      {/* Background layer: white fill + drop-shadow via SVG <filter> */}
      <svg className={`content-union-shape__bg${isPreview ? ' content-union-shape__bg--preview' : ''}`} width="100%" height="100%">
        <defs>
          <linearGradient ref={gradientRef} id="preview-bump-gradient" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="0" y2="100">
            <stop offset="0%" className="content-union-shape__gradient-start" />
            <stop offset="100%" className="content-union-shape__gradient-end" />
          </linearGradient>
          {/* SVG drop shadow equivalent to CSS drop-shadow(0 1px 3px rgba(0,0,0,0.1)) drop-shadow(0 1px 1px rgba(0,0,0,0.06)) */}
          <filter id="union-shadow" colorInterpolationFilters="sRGB" x="-5%" y="-5%" width="110%" height="110%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="1.5" result="blur1" />
            <feOffset in="blur1" dy="1" result="off1" />
            <feFlood floodColor="black" floodOpacity="0.1" result="color1" />
            <feComposite in="color1" in2="off1" operator="in" result="shadow1" />
            <feGaussianBlur in="SourceAlpha" stdDeviation="0.5" result="blur2" />
            <feOffset in="blur2" dy="1" result="off2" />
            <feFlood floodColor="black" floodOpacity="0.06" result="color2" />
            <feComposite in="color2" in2="off2" operator="in" result="shadow2" />
            <feMerge>
              <feMergeNode in="shadow1" />
              <feMergeNode in="shadow2" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <path ref={bgPathRef} filter="url(#union-shadow)" />
      </svg>
      {/* Inner shadow layer: clipped white stroke creates inset border effect */}
      <svg className="content-union-shape__inner-shadow" width="100%" height="100%">
        <defs>
          <clipPath id="inner-shadow-clip">
            <path ref={innerShadowClipRef} />
          </clipPath>
        </defs>
        <path ref={innerShadowPathRef} clipPath="url(#inner-shadow-clip)" />
      </svg>
    </div>
  )

  const borderContent = (
    <div className="content-union-shape content-union-shape--border-container">
      <svg className={`content-union-shape__border${isPreview ? ' content-union-shape__border--preview' : ''}`} width="100%" height="100%">
        <path ref={borderPathRef} />
      </svg>
    </div>
  )

  return (
    <>
      {bumpSlotEl && createPortal(bumpContent, bumpSlotEl)}
      {borderSlotEl && createPortal(borderContent, borderSlotEl)}
    </>
  )
}

export const ContentUnionShape = React.memo(ContentUnionShapeComponent)
